import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import type { CatalogCategory, CatalogRecord } from '@okmaps/shared';
import { useAppDispatch } from '../../app/hooks.js';
import { categorySelected } from '../../store/slices/uiSlice.js';
import RecordRow from './RecordRow.js';

interface Props {
  readonly categories: readonly CatalogCategory[];
  readonly records: readonly CatalogRecord[];
}

/**
 * The category accordion, which is how long-time OKMaps users navigate.
 *
 * Records are grouped in the browser rather than requested per category. The
 * catalog is small and already loaded, so expanding a category is instant
 * instead of a round trip.
 */
export default function CategoryTree({ categories, records }: Props) {
  const dispatch = useAppDispatch();

  const grouped = useMemo(() => {
    const map = new Map<string, CatalogRecord[]>();
    for (const r of records) {
      const list = map.get(r.category);
      if (list) list.push(r);
      else map.set(r.category, [r]);
    }
    for (const list of map.values()) list.sort((a, b) => a.title.localeCompare(b.title));
    return map;
  }, [records]);

  if (categories.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No datasets match the current filters.
        </Typography>
      </Box>
    );
  }

  return (
    <SimpleTreeView
      aria-label="Dataset categories"
      sx={{ py: 0.5 }}
      slotProps={{ collapseIcon: { fontSize: 'small' }, expandIcon: { fontSize: 'small' } }}
    >
      {categories.map((cat) => (
        <TreeItem
          key={cat.name}
          itemId={cat.name}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
              <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                {cat.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {cat.count}
              </Typography>
            </Box>
          }
        >
          {(grouped.get(cat.name) ?? []).map((record) => (
            <RecordRow key={record.id} record={record} />
          ))}

          <TreeItem
            itemId={`${cat.name}::all`}
            label={
              <Typography
                variant="caption"
                color="primary"
                onClick={() => dispatch(categorySelected(cat.name))}
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                View all {cat.count} as a list
              </Typography>
            }
          />
        </TreeItem>
      ))}
    </SimpleTreeView>
  );
}
